import type {Dispatch} from 'src/reducers';
import type {CanvasNodeTypes} from 'src/types/automation';

import type {
  DynamicResponse,
  AutomationModulesContext,
  ReqResVariables,
  ReqResVariable,
  SimpleVariable,
} from 'src/components/lib/automation-module-registry/automation-module-registry';
import type {
  Swimlane,
  CompositeAutomationNode,
  CompositeAutomationNodeApi,
} from 'src/types/automation-workflow';
import * as React from 'react';
import {serializeQuery} from 'src/utils/query';
import {AutomationModulesRegistryContext} from 'src/components/lib/automation-module-registry/automation-module-registry';
import {getAutomationWorkflowSwimlanes} from 'src/action-creators/automation-workflow';
import {swimlanesToGraph, useSwimlaneNode} from 'src/utils/automation-workflow';


export type UrlGenerator = {
  emailContentUrl: (
    automationWorkflowId: string,
    nodeId: string,
  ) => Promise<string | null | undefined>;
};
export const getStringWithoutAutomationIdentifiers = (str: string): string => {
  return str.replace(/{{\s*[^}]+\s*}}/g, '');
};

export function usePreviousNodeInfo(
  nodeId: string,
): DynamicResponse | null | undefined {
  const contextValue = React.useContext<
    AutomationModulesContext<SimpleVariable>
  >(AutomationModulesRegistryContext);
  const {dynamicResponseSchema} = contextValue;
  const previousNodeInfo = dynamicResponseSchema?.[nodeId] ?? null;
  return previousNodeInfo;
}
export const NON_BLACKOUT_NODES = [
  'update-record',
  'custom-note-writeback',
  'candidate-matching',
  'ai-analyze',
  'candidate-evaluation',
] as const;

export const AI_NODES = [
  'voicebot',
  'smart-scheduler',
  'candidate-matching',
  'candidate-evaluation',
] as const;

export const getUrlGenerator = (dispatch: Dispatch): UrlGenerator => {
  return {
    //NOTE:(diwakersurya) this is async because we need to fetch the swimlanes first
    // also this function has a lot of logic to handle the url generation specifically
    // to land the user to the correct node email editor page with the correct parameters
    // for now this is the only way and it is necessary to show the preview in case user
    // wants j2->j2 email content import
    emailContentUrl: async (
      automationWorkflowId: string,
      nodeId: string,
    ): Promise<string | null | undefined> => {
      const swimlanes: Swimlane[] = await dispatch(
        getAutomationWorkflowSwimlanes(automationWorkflowId),
      );

      if (swimlanes.length === 0) {
        return null;
      }
      //NOTE:(diwakersurya) for now we have only one swimlane
      const swid = swimlanes[0].id;

      const {nodes, edges} = swimlanesToGraph(swimlanes);
      const matchingNode = nodes.find((node) => node.id === nodeId);
      if (!matchingNode) {
        return null;
      }
      const edge = edges.find((edge) => edge.from === nodeId);
      if (!edge) {
        return null;
      }

      const sid = nodeId; // task node id is sid in this case
      const tid = edge.to;
      const an = nodeId;
      const prvid = nodeId;
      return `/automation-workflow/${automationWorkflowId}/email/${nodeId}/editor?${serializeQuery(
        {tid, sid, swid, an, fullScreenPanel: true, prvid},
      )}`;
    },
  };
};
export function flattenReqResVariables(
  reqResVariables: ReqResVariables,
): Map<string, ReqResVariable> {
  const flattenedVariables = new Map();

  // Iterate through each node's variables
  reqResVariables.forEach((nodeVariables) => {
    // For each node, iterate through its variable groups
    nodeVariables.forEach((variableGroup) => {
      // Add each individual variable to the flattened map
      variableGroup.forEach((variable, key) => {
        flattenedVariables.set(key, variable);
      });
    });
  });

  return flattenedVariables;
}

export const getReqResVariableName = (
  reqResVariable: ReqResVariable,
): string => {
  return `{{${reqResVariable.node_name ?? ''}.${
    reqResVariable.moduleName ?? ''
  }.${reqResVariable.title ?? ''}}}`;
};

export function extractVariableText(text: string): string | null | undefined {
  const regex = /{{\s*(.*?)\s*}}/;
  const match = text.match(regex);
  if (match && match[1]) {
    return match[1];
  } else {
    return null;
  }
}
export const isAutomationVariable = (variableId: string): boolean => {
  return variableId.startsWith('{{$.') && variableId.endsWith('}}');
};

export const extractAutomationModuleKey = (
  content: string,
): string | null | undefined => {
  if (!content) {
    return null;
  }

  // Extract chatbot_trigger_X or survey_trigger_X from patterns like
  // {{$.chatbot_trigger_X.response.long_url}} or {{$.survey_trigger_X.response.url}}
  const match = content.match(/\{\{\$\.([^.]+)\.response\.(long_url|url)\}\}/);

  return match ? match[1] : null;
};

export const getStringLengthWithAutomationIdentifiers = (
  value: string,
): number => {
  //we remove identifiers and then consider the length
  return getStringWithoutAutomationIdentifiers(value).length;
};

export const getNodeTypeFromNodeId = (nodeId: string): string => {
  const [nodeType, _] = nodeId.split('__');
  if (!nodeType) {
    throw new Error(`Invalid nodeId: ${nodeId}`);
  }
  return nodeType;
};

export type CanvasNodeProps = {
  nodeId: string;
  nodeTag: string | null | undefined;
  nodeName: string;
  swimlaneId: string;
  onNodeClick: () => unknown;
  onNodeScheduleClick: () => unknown;
  onNodeDeleteClick: () => unknown;
  onNodeDeleteAllBelowThisClick: () => unknown;
  active: boolean;
  error?: string | null | undefined;
  disabled?: boolean;
  disableContextButton?: boolean;
  scheduleSeenOnce: boolean;
  completeNodeName: string;
  hfModeExceptions: Array<string> | null | undefined;
  isHfDeleteMode: boolean;
  nodeData: CompositeAutomationNode;
  nodeType: CanvasNodeTypes;
  nodePosition: {
    x: number;
    y: number;
  };
  hoverIcon?: string | null | undefined;
  hoverText?: string | null | undefined;
  onNodeNameEdit?: (newName: string) => Promise<unknown>;
  searched: boolean;
};

//NOTE:(diwakersurya) this function is used to get the node tracking id for the current node
// it is used in the candidate evaluation node to get the node tracking id for the previous node
// because the recipient id can be calculated only using this id. Ideally beckend should own
// the logic of recipient calculation based on the node id. ask before using it.
export function useGetNodeTrackingId(): (
  arg1: string,
) => string | null | undefined {
  const automationModulesRegistryContext = React.useContext(
    AutomationModulesRegistryContext,
  );

  return (nodeId: string): string | null | undefined => {
    const previousNodeInfo =
      automationModulesRegistryContext.getPreviousNodeInfo(nodeId)?.[nodeId];
    const trackingId = previousNodeInfo?.details.previous_node_context?.key;
    return trackingId;
  };
}

export const checkIfValidCanvasNodeType = (
  nodeTypeIdentifier: string,
): boolean => {
  if (
    nodeTypeIdentifier === 'trigger' ||
    nodeTypeIdentifier === 'delay-wait' ||
    nodeTypeIdentifier === 'email' ||
    nodeTypeIdentifier === 'sms' ||
    nodeTypeIdentifier === 'candidate-matching' ||
    nodeTypeIdentifier === 'update-record' ||
    nodeTypeIdentifier === 'create-ats-record' ||
    nodeTypeIdentifier === 'custom-note-writeback' ||
    nodeTypeIdentifier === 'foreach' ||
    nodeTypeIdentifier === 'sms-chatbot' ||
    nodeTypeIdentifier === 'ai-generator' ||
    nodeTypeIdentifier === 'voicebot' ||
    nodeTypeIdentifier === 'candidate-evaluation' ||
    nodeTypeIdentifier === 'whatsapp' ||
    nodeTypeIdentifier === 'filter' ||
    nodeTypeIdentifier === 'split' ||
    nodeTypeIdentifier === 'end' ||
    nodeTypeIdentifier === 'chatbot-whatsapp' ||
    nodeTypeIdentifier === 'smart-scheduler'
  ) {
    return true;
  }
  return false;
};

/**NOTE:(diwakersurya) this hook is used to prepare the edit from the swimlane node
 * it is used to get the node data from the swimlane node and then prepare the edit
 * for the node (e.g. setting up the store).
 */
export const usePrepareEditFromSwimlaneNode = (
  swid: string,
  nodeId: string,
): {
  isPreparingEdit: boolean;
  data: CompositeAutomationNodeApi | null | undefined;
} => {
  const [isPreparingEdit, setIsPreparingEdit] = React.useState(true);
  const swimlaneNode = useSwimlaneNode(swid, nodeId);
  React.useEffect(() => {
    if (swimlaneNode) {
      setIsPreparingEdit(false);
    }
  }, [swimlaneNode]);

  return {
    isPreparingEdit,
    data: swimlaneNode,
  };
};

export const formatSpecificDateWithHoursMinutes = (date: string): string => {
  return `${date} 00:00:00`;
};

export const formatToSpecificDateFormat = (date: string): string => {
  return date.split(' ')[0];
};
